import { DocumentEditor } from "@prismicio/editor-fields"
import { useOnChange } from "@prismicio/editor-support/React"
import {
  AnimatedElement,
  type AnimatedElementProps,
  Box,
} from "@prismicio/editor-ui"
import { type ReactNode, Suspense, useState } from "react"

import { hasDocument } from "./documentData"
import { DocumentHeader } from "./DocumentHeader"
import { SettingsMenuProvider } from "./DocumentHeader/SettingsMenuProvider"
import { DocumentLayout } from "./documentLayout"
import { useDocument } from "./DocumentProvider"
import { SlicesAndContentSkeleton } from "./DocumentSkeleton"
import { DocumentTags } from "./DocumentTags"
import { LeftSidePanel } from "./LeftSidePanel"
import { RightSidePanel } from "./RightSidePanel/RightSidePanel"
import { SliceSelectionProvider } from "./SliceSelectionProvider"

const newDocumentFallbackId = "new-document"

export function Document() {
  const {
    draft,
    sectionName,
    customType,
    config,
    version,
    locale,
    documentId,
  } = useDocument()

  return (
    <SliceSelectionProvider>
      <SettingsMenuProvider>
        <DocumentLayout
          header={<DocumentHeader />}
          main={
            <>
              <AnimatedDocumentSection>
                <LeftSidePanel />

                <Box
                  padding={{ block: 24 }}
                  flexGrow={1}
                  flexDirection="column"
                  gap={8}
                >
                  <DocumentTags />
                  <DocumentEditor
                    // NOTE: Using the document's version id as the key of the DocumentEditor forces
                    // React to re-mount the editor if we load a new version. That means it populates
                    // Editor fields with the right data
                    key={version?.version_id || newDocumentFallbackId}
                    config={config}
                    customType={customType}
                    sectionName={sectionName}
                    documentId={documentId || newDocumentFallbackId}
                    content={draft.document}
                    onContentChange={draft.edit}
                    locale={locale}
                  />
                </Box>
              </AnimatedDocumentSection>

              <RightSidePanel />
            </>
          }
        />
      </SettingsMenuProvider>
    </SliceSelectionProvider>
  )
}

interface AnimatedDocumentSectionProps {
  children: ReactNode
}

function AnimatedDocumentSection(props: AnimatedDocumentSectionProps) {
  const { children } = props

  const { draft, sectionName } = useDocument()

  const [showSkeleton, setShowSkeleton] = useState(false)
  const [animation, setAnimation] = useState<Animation>({ name: "section" })

  const newKey = {
    draftId: draft.id,
    sectionName,
  }

  const [previousKey, setPreviousKey] = useState(newKey)

  if (previousKey.sectionName !== newKey.sectionName) {
    setPreviousKey(newKey)
    setAnimation({ name: "section" })
  }

  if (previousKey.draftId !== newKey.draftId) {
    setPreviousKey(newKey)
    setAnimation({ name: "content", hadSkeleton: showSkeleton })
    setShowSkeleton(false)
  }

  useOnChange(draft.nextAnimation?.toVersionId, (nextVersionId) => {
    // Only show a skeleton if we know the data is not in the cache.
    if (nextVersionId && !hasDocument(nextVersionId)) setShowSkeleton(true)
  })

  const serializedKey = Object.values(newKey).join("_")

  return (
    <Suspense fallback={<WarningFallback />}>
      <AnimatedElement
        sx={{ flexGrow: 1 }}
        animation={getElementAnimation(animation)}
        enterDuration={getElementEnterDuration(animation)}
        exitDuration={0}
      >
        <Box key={serializedKey}>
          {showSkeleton ? <SlicesAndContentSkeleton /> : children}
        </Box>
      </AnimatedElement>
    </Suspense>
  )
}

function getElementAnimation(
  animation: Animation,
): AnimatedElementProps["animation"] {
  return animation.name === "section"
    ? { name: "fade" }
    : { name: "slide", offset: -30 }
}

function getElementEnterDuration(
  animation: Animation,
): AnimatedElementProps["enterDuration"] {
  return animation.name === "section" ? 300 : 400
}

type Animation = { name: "section" } | { name: "content"; hadSkeleton: boolean }

// This Boundary should not catch any Suspense calls.
// The Page level one catches all initial data calls and the Fields/other components should handle their own data needs and boundaries.
// If this boundary catches something, it means a small component can potentially slow down the whole page
// as it means we're waiting for it before rendering anything.
function WarningFallback() {
  if (import.meta.env.DEV)
    console.warn(
      "⚠️ A Suspense Http call was made without a local Suspense Boundary.",
    )
  return null
}
